import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import { ChevronDown, X, ArrowUpDown, Search, Pencil, Trash2, Mail, Paperclip, Send, FileSpreadsheet, Users } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { contactsAPI, employeesAPI } from "../../../services/api";
import { bulkEmailAPI, templatesAPI } from "../../../services/api";
import { getWhatsAppNumber, getWhatsAppNumberSource } from '../../Templates/templateUtils';
import EmailTemplatePanel from '../../Templates/EmailTemplatePanel';
import WhatsAppTemplatePanel from '../../Templates/WhatsAppTemplatePanel';
import { exportContactsToExcel } from "../../../utils/ExportUtils";
import Delete from "../../common/Delete";
import Assign from "../../common/AssignModal";
import Edit from "../../common/EditModal";
import CreateContactModal from "./CreateContactModal";
import ImportContactsModal from "./ImportContactsModal";
import EditContactModal from "./EditContactModal";
import PageNavigator from "../../common/PageNavigator";
import { toast } from "react-toastify";

const BRAND = "#1C4E6D";
const BRAND_DARK = "#153a4f";
const PAGE_SIZE = 20;

// ── BulkEmailModal ───────────────────────────────────────────────────────────
const BulkEmailModal = ({ selectedCount, selectedIds, entityType, onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const fileRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByIds(entityType, selectedIds, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-0.5">Bulk Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Users size={16} className="text-slate-400" />
              Send to {selectedCount} selected {selectedCount === 1 ? 'contact' : 'contacts'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Template picker */}
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject <span className="text-red-500">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message <span className="text-red-500">*</span></label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
            <div onClick={() => fileRef.current.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <Paperclip size={15} className="text-slate-400" /><span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSend} disabled={sending} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</> : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── ExcelEmailModal ──────────────────────────────────────────────────────────
const ExcelEmailModal = ({ entityType, onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [excelFile, setExcelFile]     = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const excelRef  = useRef();
  const attachRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!excelFile)      { toast.error('Please upload an Excel file'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByExcel(entityType, excelFile, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      if (d.excelRowErrors?.length > 0) toast.info(`${d.excelRowErrors.length} row(s) skipped from Excel (missing/invalid email)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-0.5">Excel Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-slate-400" />Send Email via Excel Upload
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Excel must have an <strong>email</strong> column</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Excel File (.xlsx / .xls) <span className="text-red-500">*</span></label>
            {excelFile ? (
              <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2"><FileSpreadsheet size={15} className="text-emerald-600" /><span className="text-sm font-medium text-emerald-800 truncate">{excelFile.name}</span></div>
                <button onClick={() => setExcelFile(null)} className="text-red-400 hover:text-red-600 ml-2"><X size={14} /></button>
              </div>
            ) : (
              <div onClick={() => excelRef.current.click()} className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                <FileSpreadsheet size={24} className="text-slate-300" />
                <span className="text-sm text-slate-400">Click to upload Excel file</span>
                <span className="text-xs text-slate-300">.xlsx or .xls only</span>
              </div>
            )}
            <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => e.target.files[0] && setExcelFile(e.target.files[0])} />
          </div>
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject <span className="text-red-500">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message <span className="text-red-500">*</span></label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
            <div onClick={() => attachRef.current.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
              <Paperclip size={15} className="text-slate-400" /><span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={attachRef} type="file" multiple className="hidden" onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSend} disabled={sending} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</> : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Contact Component ───────────────────────────────────────────────────
const Contact = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('All contacts');
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showExcelEmailModal, setShowExcelEmailModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedContactsForEdit, setSelectedContactsForEdit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showSingleEditModal, setShowSingleEditModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [employeeNameCache, setEmployeeNameCache] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [emailPanelContact, setEmailPanelContact] = useState(null);
  const [whatsappPanelContact, setWhatsappPanelContact] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('contactViewColumns');
    return saved ? JSON.parse(saved).filter(k => k !== 'lastName') : [
      'firstName', 'phone', 'email', 'companyName',
      'jobTitle', 'department', 'leadStatus', 'leadSource', 'assignedTo'
    ];
  });

  const allColumns = [
    { key: 'firstName',   label: 'Name',   required: true },
    { key: 'phone',       label: 'Phone' },
    { key: 'email',       label: 'Email' },
    { key: 'companyName', label: 'Company Name' },
    { key: 'company_id',  label: 'Company ID' },
    { key: 'jobTitle',    label: 'Job Title' },
    { key: 'department',  label: 'Department' },
    { key: 'leadStatus',  label: 'Lead Status' },
    { key: 'leadSource',  label: 'Lead Source' },
    { key: 'linkedInUrl', label: 'LinkedIn URL' },
    { key: 'assignedTo',  label: 'Assigned To' },
    { key: 'createdAt',   label: 'Created Date' }
  ];

  const [filters, setFilters] = useState({
    department: null, leadStatus: null, leadSource: null,
    firstName: '', lastName: '', email: '', phone: '',
    companyName: '', jobTitle: '', assignedTo: null
  });

  const tabs = ['All contacts', 'My contacts'];

  const departmentOptions = [
    { value: 'EXECUTIVE_LEADERSHIP', label: 'Executive Leadership' },
    { value: 'HUMAN_RESOURCES', label: 'Human Resources' },
    { value: 'TALENT_ACQUISITION', label: 'Talent Acquisition' },
    { value: 'SALES', label: 'Sales' }, { value: 'MARKETING', label: 'Marketing' },
    { value: 'CUSTOMER_SUPPORT', label: 'Customer Support' }, { value: 'FINANCE', label: 'Finance' },
    { value: 'LEGAL', label: 'Legal' }, { value: 'IT', label: 'IT' },
    { value: 'ENGINEERING', label: 'Engineering' },
    { value: 'PRODUCT_MANAGEMENT', label: 'Product Management' },
    { value: 'DESIGN_UX', label: 'Design & UX' }, { value: 'OPERATIONS', label: 'Operations' },
    { value: 'BUSINESS_DEVELOPMENT', label: 'Business Development' },
    { value: 'PROCUREMENT', label: 'Procurement' }, { value: 'MANUFACTURING', label: 'Manufacturing' },
    { value: 'LOGISTICS', label: 'Logistics' }, { value: 'QUALITY_ASSURANCE', label: 'Quality Assurance' },
    { value: 'RESEARCH_DEVELOPMENT', label: 'Research & Development' },
    { value: 'TRAINING_LD', label: 'Training & L&D' }, { value: 'SECURITY_RISK', label: 'Security & Risk' },
    { value: 'PR_COMMUNICATIONS', label: 'PR & Communications' },
    { value: 'FACILITIES_MANAGEMENT', label: 'Facilities Management' },
    { value: 'CONSULTING', label: 'Consulting' }, { value: 'HEALTHCARE', label: 'Healthcare' },
    { value: 'EDUCATION', label: 'Education' }, { value: 'DATA_ANALYTICS', label: 'Data & Analytics' },
    { value: 'INVESTOR_RELATIONS', label: 'Investor Relations' },
    { value: 'CORPORATE_STRATEGY', label: 'Corporate Strategy' }, { value: 'OTHER', label: 'Other' },
  ];
  const leadStatusOptions = [
    { value: 'NEW', label: 'New' }, { value: 'PROSPECT', label: 'Prospect' },
    { value: 'CONTACTED', label: 'Contacted' }, { value: 'QUALIFIED_LEAD', label: 'Qualified Lead' },
    { value: 'UNQUALIFIED_LEAD', label: 'Unqualified Lead' }, { value: 'NURTURING', label: 'Nurturing' },
    { value: 'MEETING_SCHEDULED', label: 'Meeting Scheduled' }, { value: 'PROPOSAL_SENT', label: 'Proposal Sent' },
    { value: 'NEGOTIATION', label: 'Negotiation' }, { value: 'CONTRACT_SENT', label: 'Contract Sent' },
    { value: 'WON_CUSTOMER', label: 'Won Customer' }, { value: 'LOST', label: 'Lost' },
    { value: 'ON_HOLD', label: 'On Hold' }, { value: 'OTHER', label: 'Other' },
  ];
  const leadSourceOptions = [
    { value: 'GOOGLE_ADS', label: 'Google Ads' }, { value: 'LINKEDIN_ADS', label: 'LinkedIn Ads' },
    { value: 'FACEBOOK_ADS', label: 'Facebook Ads' }, { value: 'INSTAGRAM_ADS', label: 'Instagram Ads' },
    { value: 'WHATSAPP_ADS', label: 'WhatsApp Ads' }, { value: 'TELEGRAM_ADS', label: 'Telegram Ads' },
    { value: 'CONTACT_FORM', label: 'Contact Form' }, { value: 'EMAIL_CAMPAIGN', label: 'Email Campaign' },
    { value: 'COLD_CALLING', label: 'Cold Calling' }, { value: 'REFERRAL', label: 'Referral' },
    { value: 'EVENTS', label: 'Events' }, { value: 'TRADE_SHOWS', label: 'Trade Shows' },
    { value: 'PARTNERSHIP', label: 'Partnership' }, { value: 'ORGANIC_SEARCH', label: 'Organic Search' },
    { value: 'PAPER_ADS', label: 'Paper Ads' }, { value: 'OTHER', label: 'Other' },
  ];

  const getStatusColor = (type, value) => {
    const colors = {
      leadStatus: {
        NEW: 'bg-blue-100 text-blue-800', PROSPECT: 'bg-cyan-100 text-cyan-800',
        CONTACTED: 'bg-indigo-100 text-indigo-800', QUALIFIED_LEAD: 'bg-green-100 text-green-800',
        UNQUALIFIED_LEAD: 'bg-red-100 text-red-800', NURTURING: 'bg-yellow-100 text-yellow-800',
        MEETING_SCHEDULED: 'bg-purple-100 text-purple-800', PROPOSAL_SENT: 'bg-violet-100 text-violet-800',
        NEGOTIATION: 'bg-orange-100 text-orange-800', CONTRACT_SENT: 'bg-teal-100 text-teal-800',
        WON_CUSTOMER: 'bg-green-500 text-white', LOST: 'bg-red-200 text-red-800',
        ON_HOLD: 'bg-gray-200 text-gray-700', OTHER: 'bg-gray-100 text-gray-800',
      },
      leadSource: {
        GOOGLE_ADS: 'bg-red-100 text-red-800', LINKEDIN_ADS: 'bg-blue-100 text-blue-800',
        FACEBOOK_ADS: 'bg-indigo-100 text-indigo-800', INSTAGRAM_ADS: 'bg-pink-100 text-pink-800',
        WHATSAPP_ADS: 'bg-green-100 text-green-800', TELEGRAM_ADS: 'bg-cyan-100 text-cyan-800',
        CONTACT_FORM: 'bg-purple-100 text-purple-800', EMAIL_CAMPAIGN: 'bg-violet-100 text-violet-800',
        COLD_CALLING: 'bg-gray-100 text-gray-800', REFERRAL: 'bg-teal-100 text-teal-800',
        EVENTS: 'bg-orange-100 text-orange-800', TRADE_SHOWS: 'bg-yellow-100 text-yellow-800',
        PARTNERSHIP: 'bg-lime-100 text-lime-800', ORGANIC_SEARCH: 'bg-emerald-100 text-emerald-800',
        PAPER_ADS: 'bg-amber-100 text-amber-800', OTHER: 'bg-gray-100 text-gray-800',
      },
      department: {
        EXECUTIVE_LEADERSHIP: 'bg-red-100 text-red-800', HUMAN_RESOURCES: 'bg-pink-100 text-pink-800',
        TALENT_ACQUISITION: 'bg-rose-100 text-rose-800', SALES: 'bg-green-100 text-green-800',
        MARKETING: 'bg-purple-100 text-purple-800', CUSTOMER_SUPPORT: 'bg-teal-100 text-teal-800',
        FINANCE: 'bg-yellow-100 text-yellow-800', LEGAL: 'bg-orange-100 text-orange-800',
        IT: 'bg-blue-100 text-blue-800', ENGINEERING: 'bg-indigo-100 text-indigo-800',
        PRODUCT_MANAGEMENT: 'bg-violet-100 text-violet-800', DESIGN_UX: 'bg-fuchsia-100 text-fuchsia-800',
        OPERATIONS: 'bg-amber-100 text-amber-800', BUSINESS_DEVELOPMENT: 'bg-lime-100 text-lime-800',
        PROCUREMENT: 'bg-cyan-100 text-cyan-800', MANUFACTURING: 'bg-gray-100 text-gray-800',
        LOGISTICS: 'bg-sky-100 text-sky-800', QUALITY_ASSURANCE: 'bg-emerald-100 text-emerald-800',
        RESEARCH_DEVELOPMENT: 'bg-blue-200 text-blue-900', TRAINING_LD: 'bg-purple-200 text-purple-900',
        SECURITY_RISK: 'bg-red-200 text-red-900', PR_COMMUNICATIONS: 'bg-pink-200 text-pink-900',
        FACILITIES_MANAGEMENT: 'bg-gray-200 text-gray-700', CONSULTING: 'bg-teal-200 text-teal-900',
        HEALTHCARE: 'bg-green-200 text-green-900', EDUCATION: 'bg-yellow-200 text-yellow-900',
        DATA_ANALYTICS: 'bg-indigo-200 text-indigo-900', INVESTOR_RELATIONS: 'bg-orange-200 text-orange-900',
        CORPORATE_STRATEGY: 'bg-violet-200 text-violet-900', OTHER: 'bg-gray-100 text-gray-800',
      },
    };
    return colors[type]?.[value] || 'bg-gray-100 text-gray-800';
  };

  const formatValue = (key, value) => {
    if (!value) return '';
    if (['department', 'leadStatus', 'leadSource'].includes(key)) return value.replace(/_/g, ' ');
    return value;
  };

  useEffect(() => {
    const ids = [...new Set(contacts.map(c => c.assignedTo).filter(Boolean))];
    const uncachedIds = ids.filter(id => !(id in employeeNameCache));
    if (uncachedIds.length === 0) return;
    uncachedIds.forEach(async (id) => {
      try {
        const response = await employeesAPI.getEmployeeById(id);
        const emp = response.data;
        const name = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || id;
        setEmployeeNameCache(prev => ({ ...prev, [id]: name }));
      } catch { setEmployeeNameCache(prev => ({ ...prev, [id]: id })); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  const fetchEmployeeOptions = async () => {
    try {
      const response = await employeesAPI.getAllEmployees();
      const options = (response.data || []).map(emp => ({
        value: emp.id,
        label: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id
      }));
      setEmployeeOptions(options);
    } catch (error) { console.error('Failed to fetch employees:', error); }
  };

  const fetchContactsPaged = async (page) => {
    setLoading(true);
    try {
      const params = {
        page, size: PAGE_SIZE,
        search: searchQuery || undefined,
        department: filters.department?.value,
        leadStatus: filters.leadStatus?.value,
        leadSource: filters.leadSource?.value,
        firstName: filters.firstName || undefined,
        lastName: filters.lastName || undefined,
        email: filters.email || undefined,
        phone: filters.phone || undefined,
        companyName: filters.companyName || undefined,
        jobTitle: filters.jobTitle || undefined,
        assignedTo: filters.assignedTo?.value,
        ...(activeTab === 'My contacts' ? { scope: 'mine' } : {})
      };
      const response = await contactsAPI.getContactsPaged(params);
      setContacts(response.data.content || []);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) { toast.error("Failed to fetch contacts"); }
    finally { setLoading(false); }
  };

  const refreshContacts = () => fetchContactsPaged(currentPage);

  useEffect(() => { fetchEmployeeOptions(); }, []);

  useEffect(() => {
    fetchContactsPaged(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, searchQuery, currentPage]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };
  const clearAllFilters = () => {
    setFilters({ department: null, leadStatus: null, leadSource: null, firstName: '', lastName: '', email: '', phone: '', companyName: '', jobTitle: '', assignedTo: null });
    setCurrentPage(0);
  };
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(0);
  };

  const handleSelectAll = (e) => setSelectedRows(e.target.checked ? contacts.map(c => c.id) : []);
  const handleRowSelect = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const handleExport = () => { const visibleCols = allColumns.filter(col => visibleColumns.includes(col.key)); exportContactsToExcel(contacts, visibleCols); };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;
    try {
      const ids = getSelectedIds();
      await contactsAPI.bulkDelete(ids);
      toast.success(`${selectedRows.length} contact(s) deleted successfully`);
      setSelectedRows([]); setShowDeleteModal(false); refreshContacts();
    } catch (error) { toast.error(error.response?.data?.message || error.message || "Failed to delete contacts"); }
  };

  const handleAssign = async (owner) => {
    if (selectedRows.length === 0) return;
    try {
      const ids = getSelectedIds();
      await contactsAPI.updateContactOwner({ ids, assignedTo: owner });
      toast.success(`${selectedRows.length} contact(s) assigned successfully`);
      setSelectedRows([]); setShowAssignModal(false); refreshContacts();
    } catch (error) { toast.error("Failed to assign contacts"); }
  };

  const handleEdit = async (fieldName, fieldValue) => {
    if (selectedRows.length === 0) return;
    try {
      const ids = getSelectedIds();
      const res = await contactsAPI.bulkEdit({ ids, fieldName, fieldValue });
      const r = res.data || {};
      if (r.failedCount > 0) toast.warning(`${r.successCount} updated, ${r.failedCount} failed`);
      else toast.success(`${r.successCount ?? ids.length} contact(s) updated successfully`);
      setSelectedRows([]); setShowEditModal(false); refreshContacts();
    } catch (error) { toast.error(error.response?.data?.message || "Failed to edit contacts"); }
  };

  const handleRowEditClick = (contact, e) => { e.stopPropagation(); setContactToEdit(contact); setShowSingleEditModal(true); };
  const handleRowDeleteClick = (contact, e) => { e.stopPropagation(); setContactToDelete(contact); setShowSingleDeleteModal(true); };

  const handleSingleContactUpdate = async (id, data) => {
    try {
      await contactsAPI.updateContact(id, data);
      toast.success("Contact updated successfully");
      setShowSingleEditModal(false); setContactToEdit(null); refreshContacts();
    } catch (error) { toast.error(error.response?.data?.message || error.message || "Failed to update contact"); }
  };

  const handleSingleContactDelete = async () => {
    if (!contactToDelete) return;
    try {
      await contactsAPI.deleteContact(contactToDelete.id);
      toast.success("Contact deleted successfully");
      setShowSingleDeleteModal(false); setContactToDelete(null); refreshContacts();
    } catch (error) { toast.error(error.response?.data?.message || error.message || "Failed to delete contact"); }
  };

  const enumFields = { department: departmentOptions, leadStatus: leadStatusOptions, leadSource: leadSourceOptions };
  const getOptionsForColumn = (columnKey) => enumFields[columnKey] || null;
  const isEnumField = (columnKey) => columnKey in enumFields;

  const handleCellDoubleClick = (contact, columnKey) => {
    if (['firstName', 'id', 'createdAt', 'updatedAt'].includes(columnKey)) return;
    setEditingCell({ rowId: contact.id, columnKey }); setEditingValue(contact[columnKey] || '');
  };
  const handleCellInputChange = (e) => setEditingValue(e.target.value);
  const handleCellSelectChange = (value) => setEditingValue(value);

  const handleCellSave = async () => {
    if (!editingCell) return;
    try {
      const contact = contacts.find(c => c.id === editingCell.rowId);
      if (!contact) return;
      await contactsAPI.updateContact(contact.id, { [editingCell.columnKey]: editingValue });
      setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, [editingCell.columnKey]: editingValue } : c));
      toast.success('Field updated successfully');
      setEditingCell(null); setEditingValue('');
    } catch (error) { toast.error('Failed to update field: ' + (error.response?.data?.message || error.message)); setEditingCell(null); }
  };
  const handleCellCancel = () => { setEditingCell(null); setEditingValue(''); };
  const isEditingCell = (contactId, columnKey) => editingCell?.rowId === contactId && editingCell?.columnKey === columnKey;

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => {
      const next = prev.includes(columnKey) ? prev.filter(k => k !== columnKey) : [...prev, columnKey];
      localStorage.setItem('contactViewColumns', JSON.stringify(next)); return next;
    });
  };
  const handleSelectAllColumns = () => { const allKeys = allColumns.map(c => c.key); setVisibleColumns(allKeys); localStorage.setItem('contactViewColumns', JSON.stringify(allKeys)); };
  const handleDeselectAllColumns = () => { const required = allColumns.filter(c => c.required).map(c => c.key); setVisibleColumns(required); localStorage.setItem('contactViewColumns', JSON.stringify(required)); };
  const isColumnVisible = (key) => visibleColumns.includes(key);

  // Get contact IDs for selected rows
  const getSelectedIds = () => selectedRows;

  const renderCellContent = (column, contact) => {
    switch (column.key) {
      case 'firstName': {
        const fName = contact.firstName || '';
        const lName = contact.lastName || '';
        const displayName = [fName, lName].filter(Boolean).join(' ');
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: BRAND }}
            >
              {fName ? fName.charAt(0).toUpperCase() : '?'}
            </div>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/contact/${contact.id}`); }}
              className="text-sm font-medium hover:underline cursor-pointer"
              style={{ color: BRAND }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_DARK}
              onMouseLeave={e => e.currentTarget.style.color = BRAND}
            >
              {displayName || <span className="text-gray-400 italic">Unnamed</span>}
            </button>
          </div>
        );
      }
      case 'assignedTo': {
        const id = contact.assignedTo;
        if (!id) return <span className="text-gray-400">—</span>;
        const name = employeeNameCache[id];
        if (!name) return <span className="flex items-center gap-1 text-gray-400 text-xs"><div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />Loading...</span>;
        return <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{name.charAt(0).toUpperCase()}</div><span className="text-sm text-gray-800">{name}</span></div>;
      }
      case 'linkedInUrl':
        return contact.linkedInUrl ? <a href={contact.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">View Profile</a> : null;
      case 'department': case 'leadStatus': case 'leadSource':
        return contact[column.key] ? <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(column.key, contact[column.key])}`}>{formatValue(column.key, contact[column.key])}</span> : null;
      case 'email':
        return contact.email ? (
          <div className="flex items-center gap-1.5">
            <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-700 hover:underline">{contact.email}</a>
            <button onClick={e => { e.stopPropagation(); setEmailPanelContact(contact); }} title="Send email from template"
              className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-blue-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
              <Mail size={13} />
            </button>
          </div>
        ) : null;
      case 'phone':
        return contact.phone ? (
          <div className="flex items-center gap-1.5">
            <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-700 hover:underline">{contact.phone}</a>
            <button onClick={e => { e.stopPropagation(); if (getWhatsAppNumber(contact)) setWhatsappPanelContact(contact); }}
              title={getWhatsAppNumber(contact) ? 'Send WhatsApp' : 'Not a valid WhatsApp number'}
              disabled={!getWhatsAppNumber(contact)}
              className={`opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded ${getWhatsAppNumber(contact) ? 'text-[#25D366] hover:bg-green-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}>
              <IoLogoWhatsapp size={13} />
            </button>
          </div>
        ) : null;
      case 'createdAt':
        return contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : 'N/A';
      default:
        return contact[column.key] || '—';
    }
  };

  return (
    <div>
      {/* ── Email Modals ── */}
      {showBulkEmailModal && (
        <BulkEmailModal
          selectedCount={selectedRows.length}
          selectedIds={getSelectedIds()}
          entityType="CONTACT"
          onClose={() => setShowBulkEmailModal(false)}
        />
      )}
      {showExcelEmailModal && (
        <ExcelEmailModal entityType="CONTACT" onClose={() => setShowExcelEmailModal(false)} />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white border-b gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">Contacts <ChevronDown size={20} className="text-gray-500" /></h1>
          <span className="text-xs sm:text-sm text-gray-600">{totalElements} records</span>
          {loading && <span className="text-xs sm:text-sm text-blue-600">Loading...</span>}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button onClick={() => setShowExcelEmailModal(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap">
             Bulk Email
          </button>
          <button onClick={() => setShowImportModal(true)} className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap">Import</button>
          <button onClick={() => setShowCreateModal(true)} className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap">Create</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSelectedRows([]); setCurrentPage(0); }}
            className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tab}
            {tab === 'All contacts' && activeTab === tab && <X className="ml-2 inline-block w-4 h-4 text-gray-400" />}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-4 px-4 sm:px-6">
          <button onClick={() => setShowColumnSelector(true)} className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap">
            <span className="hidden sm:inline">Customize Columns</span><span className="sm:hidden">Columns</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-48"><Select options={departmentOptions} value={filters.department} onChange={v => handleFilterChange('department', v)} placeholder="Department" isClearable className="text-sm" /></div>
            <div className="w-48"><Select options={leadStatusOptions} value={filters.leadStatus} onChange={v => handleFilterChange('leadStatus', v)} placeholder="Lead Status" isClearable className="text-sm" /></div>
            <div className="w-48"><Select options={leadSourceOptions} value={filters.leadSource} onChange={v => handleFilterChange('leadSource', v)} placeholder="Lead Source" isClearable className="text-sm" /></div>
            <button onClick={() => setShowMoreFilters(!showMoreFilters)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">{showMoreFilters ? 'Less filters' : 'More filters'}</button>
          </div>
          {Object.values(filters).some(f => f) && <button onClick={clearAllFilters} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Clear all filters</button>}
        </div>
        {showMoreFilters && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <Select options={employeeOptions} value={filters.assignedTo} onChange={v => handleFilterChange('assignedTo', v)} placeholder="Assigned To" isClearable className="text-sm" />
            {[{key:'firstName',placeholder:'First Name'},{key:'lastName',placeholder:'Last Name'},{key:'email',placeholder:'Email'},{key:'phone',placeholder:'Phone'},{key:'companyName',placeholder:'Company Name'},{key:'jobTitle',placeholder:'Job Title'}].map(({key,placeholder}) => (
              <input key={key} type="text" placeholder={placeholder} value={filters[key]} onChange={e => handleFilterChange(key, e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            ))}
          </div>
        )}
      </div>

      {/* Search and Bulk Actions */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Search name, phone, or email" value={searchQuery} onChange={e => handleSearchChange(e.target.value)}
            autoComplete="off" name="contact-search"
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div className="flex items-center gap-3 ml-4">
          <button onClick={handleExport} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold">Export</button>
          <button onClick={() => { if (selectedRows.length === 0) { toast.warning("Please select at least one contact to edit"); return; } setSelectedContactsForEdit(contacts.filter(c => selectedRows.includes(c.id))); setShowEditModal(true); }}
            className="px-4 py-2 text-sm bg-[#1C4E6D] text-white rounded-md hover:bg-[#153a4f] transition-colors">Edit</button>
          <button onClick={() => { if (selectedRows.length === 0) { toast.warning("Please select at least one contact to delete"); return; } setShowDeleteModal(true); }}
            className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors">Delete</button>
          <button onClick={() => { if (selectedRows.length === 0) { toast.warning("Please select at least one contact to assign"); return; } setShowAssignModal(true); }}
            className="px-4 py-2 text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors">Assign</button>
          {selectedRows.length > 0 && (
            <button onClick={() => setShowBulkEmailModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors whitespace-nowrap">
              <Mail size={14} /> Send Email ({selectedRows.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input type="checkbox" checked={selectedRows.length === contacts.length && contacts.length > 0} onChange={handleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              </th>
              {allColumns.filter(col => isColumnVisible(col.key)).map(column => (
                <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center gap-2">{column.label}<ArrowUpDown size={14} /></div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.length === 0 ? (
              <tr><td colSpan={allColumns.filter(col => isColumnVisible(col.key)).length + 2} className="px-6 py-4 text-center text-gray-500">No contacts found</td></tr>
            ) : (
              contacts.map((contact, index) => (
                <tr key={contact.id || index}
                  className={`group transition-colors ${selectedRows.includes(contact.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  onMouseEnter={() => setHoveredRow(contact.id)} onMouseLeave={() => setHoveredRow(null)}>
                  <td className="px-6 py-4">
                    <input type="checkbox" checked={selectedRows.includes(contact.id)} onChange={() => handleRowSelect(contact.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  </td>
                  {allColumns.filter(col => isColumnVisible(col.key)).map(column => {
                    const isEnum = isEnumField(column.key);
                    const options = getOptionsForColumn(column.key);
                    return (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-text hover:bg-blue-50 transition-colors" onDoubleClick={() => handleCellDoubleClick(contact, column.key)}>
                        {isEditingCell(contact.id, column.key) ? (
                          <div className="flex items-center gap-1">
                            {isEnum ? (
                              <select value={editingValue} onChange={e => handleCellSelectChange(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }} className="flex-1 px-2 py-1 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus>
                                <option value="">Select {column.label}</option>
                                {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                            ) : (
                              <input type="text" value={editingValue} onChange={handleCellInputChange} onKeyDown={e => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }} className="flex-1 px-2 py-1 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
                            )}
                            <button onClick={handleCellSave} className="text-green-600 hover:text-green-700 font-bold text-xs" title="Save (Enter)">&#10003;</button>
                            <button onClick={handleCellCancel} className="text-red-600 hover:text-red-700 font-bold text-xs" title="Cancel (Esc)">&#x2715;</button>
                          </div>
                        ) : renderCellContent(column, contact)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={e => handleRowEditClick(contact, e)} title="Edit contact" className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100 transition-all duration-150">
                        <Pencil size={18} />
                      </button>
                      <button onClick={e => handleRowDeleteClick(contact, e)} title="Delete contact" className="p-1.5 rounded-md text-red-600 hover:bg-red-100 transition-all duration-150">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <PageNavigator
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b"><h3 className="text-lg font-semibold">Edit columns</h3><button onClick={() => setShowColumnSelector(false)}><X size={20} /></button></div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <button onClick={handleSelectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Select all</button>
                <button onClick={handleDeselectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Deselect all</button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allColumns.map(column => (
                  <label key={column.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={isColumnVisible(column.key)} onChange={() => !column.required && handleColumnToggle(column.key)} disabled={column.required} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm">{column.label}</span>
                    {column.required && <span className="text-xs text-gray-500">(Required)</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end"><button onClick={() => setShowColumnSelector(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">Done</button></div>
          </div>
        </div>
      )}

      {/* Single delete confirm */}
      {showSingleDeleteModal && contactToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100"><h3 className="text-base font-semibold text-gray-900">Delete Contact</h3></div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><Trash2 size={18} className="text-red-600" /></div>
                <div>
                  <p className="text-sm text-gray-700">Are you sure you want to delete <span className="font-semibold text-gray-900">{contactToDelete.firstName} {contactToDelete.lastName}</span>?</p>
                  <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => { setShowSingleDeleteModal(false); setContactToDelete(null); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">Cancel</button>
              <button onClick={handleSingleContactDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && <Delete onClose={() => setShowDeleteModal(false)} onConfirm={handleDelete} count={selectedRows.length} />}
      {showAssignModal && <Assign onClose={() => setShowAssignModal(false)} onConfirmAssign={handleAssign} selectedCount={selectedRows.length} />}
      {showEditModal && <Edit onClose={() => setShowEditModal(false)} onSave={handleEdit} selectedCount={selectedRows.length} />}
      {showCreateModal && <CreateContactModal onClose={() => setShowCreateModal(false)} onCreate={refreshContacts} />}
      {showImportModal && <ImportContactsModal onClose={() => setShowImportModal(false)} onImport={refreshContacts} />}
      {showSingleEditModal && contactToEdit && (
        <EditContactModal contact={contactToEdit} onClose={() => { setShowSingleEditModal(false); setContactToEdit(null); }} onSave={handleSingleContactUpdate} />
      )}

      <EmailTemplatePanel
        isOpen={Boolean(emailPanelContact)}
        onClose={() => setEmailPanelContact(null)}
        module="CRM"
        context={emailPanelContact ? {
          contactName: `${emailPanelContact.firstName || ''} ${emailPanelContact.lastName || ''}`.trim(),
          companyName: emailPanelContact.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={emailPanelContact ? {
          email: emailPanelContact.email,
          name: `${emailPanelContact.firstName || ''} ${emailPanelContact.lastName || ''}`.trim(),
        } : null}
      />
      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelContact)}
        onClose={() => setWhatsappPanelContact(null)}
        module="CRM"
        context={whatsappPanelContact ? {
          contactName: `${whatsappPanelContact.firstName || ''} ${whatsappPanelContact.lastName || ''}`.trim(),
          companyName: whatsappPanelContact.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelContact ? {
          phone: getWhatsAppNumber(whatsappPanelContact),
          phoneSource: getWhatsAppNumberSource(whatsappPanelContact),
          name: `${whatsappPanelContact.firstName || ''} ${whatsappPanelContact.lastName || ''}`.trim(),
        } : null}
      />
    </div>
  );
};

export default Contact;